import React from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Link,
  Card,
  CardContent,
  Divider,
  Grid,
  Rating,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { getPeriod } from "../../../../../components/LibraryFunctions";
import ComparisonToggle from "../../../../../components/ComparisonToggle";
import PreviousReportSelector from "../../../../../components/PreviousReportSelector";
import { analyzeWeight } from "../../../../../utils/weightAnalysis";
import useReportCompliance from "../../../../../hooks/useReportCompliance";
import ComplianceTrendIndicator from "../../../../../components/ComplianceTrendIndicator";

import StandardButton from "../../../../../components/StandardButton";

const HeaderCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  marginBottom: theme.spacing(3),
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor:
    status === "unprocessed"
      ? theme.palette.warning.main
      : theme.palette.success.main,
  color: "white",
  fontWeight: "bold",
}));

const MetricCard = styled(Card)(({ theme }) => ({
  height: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: theme.shadows[4],
  },
}));

const ReportHeader = ({ report, onClientClick, handleMarkProcessed }) => {
  if (!report) return null;

  const period = getPeriod(report);
  const { clientData, status, healthData } = report;
  const compliance = useReportCompliance(report);

  // Debug logging
  console.log("ReportHeader - report:", {
    uid: report.uid,
    organizationId: report.organizationId,
    hasExerciseData: !!(report.exerciseData && report.exerciseData.length > 0),
    hasHealthData: !!(report.healthData && report.healthData.length > 0),
    hasSupplementData: !!(
      report.supplementData && report.supplementData.length > 0
    ),
    hasWeeklyOptionals: !!(
      report.weeklyOptionals && report.weeklyOptionals.length > 0
    ),
  });

  console.log("ReportHeader - compliance:", compliance);

  const weightAnalysis = analyzeWeight(healthData, clientData.statistics);

  return (
    <Box>
      <HeaderCard>
        <CardContent sx={{ p: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Stack spacing={1.5}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h4" fontWeight="bold">
                  Report: {period}
                </Typography>
                <Link
                  component="button"
                  variant="body1"
                  onClick={onClientClick}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    color: (theme) => theme.palette.primary.main,
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.action.hover,
                      borderColor: (theme) => theme.palette.primary.main,
                    },
                  }}
                >
                  View Client:{" "}
                  {`${report.clientFirstName} ${report.clientLastName}`}
                </Link>
              </Box>
              <Stack spacing={1} alignItems="flex-start">
                <Stack direction="row" spacing={2} alignItems="center">
                  <StatusChip
                    status={status}
                    label={
                      status === "unprocessed" ? "Unprocessed" : "Processed"
                    }
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Submitted:{" "}
                    {new Date(report.createdOn).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Stack>
                {/* Temporarily hidden - Compare feature not fully developed
                <Stack direction="row" spacing={1} alignItems="center">
                  <ComparisonToggle />
                  <PreviousReportSelector />
                </Stack>
                */}
              </Stack>
            </Stack>
            <Stack spacing={2} alignItems="flex-end">
              <StandardButton
                variant="primary"
                onClick={() => handleMarkProcessed(report)}
                sx={{ borderRadius: "20px" }}
              >
                {status === "unprocessed"
                  ? "Mark as Processed"
                  : "Mark as Unprocessed"}
              </StandardButton>
            </Stack>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md>
              <MetricCard>
                <CardContent sx={{ p: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Protocol
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {report.protocol?.name || "Not Assigned"}
                  </Typography>
                </CardContent>
              </MetricCard>
            </Grid>
            <Grid item xs={6} sm={4} md>
              <MetricCard>
                <CardContent sx={{ p: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Weight Summary
                  </Typography>
                  {weightAnalysis.hasData ? (
                    <Stack spacing={0.5}>
                      {/* Latest Weight */}
                      {weightAnalysis.latestWeight && (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body2" color="text.secondary">
                            Latest:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {Math.round(weightAnalysis.latestWeight)} lbs
                          </Typography>
                        </Stack>
                      )}

                      {/* Average Weight */}
                      {weightAnalysis.averageWeight &&
                        weightAnalysis.totalDataPoints > 1 && (
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="body2" color="text.secondary">
                              Period Avg:
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                              {Math.round(weightAnalysis.averageWeight)} lbs
                            </Typography>
                          </Stack>
                        )}

                      {/* Starting Weight */}
                      {weightAnalysis.startingWeight && (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body2" color="text.secondary">
                            Starting:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {Math.round(weightAnalysis.startingWeight)} lbs
                          </Typography>
                        </Stack>
                      )}

                      {/* Weight Change */}
                      {weightAnalysis.startingWeight &&
                        weightAnalysis.latestWeight && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textAlign: "center" }}
                          >
                            {weightAnalysis.startingWeight -
                              weightAnalysis.latestWeight >
                            0
                              ? `↓ ${Math.round(weightAnalysis.startingWeight - weightAnalysis.latestWeight)} lbs lost`
                              : `↑ ${Math.round(weightAnalysis.latestWeight - weightAnalysis.startingWeight)} lbs gained`}
                          </Typography>
                        )}

                      {/* Data Points */}
                      {weightAnalysis.totalDataPoints > 0 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textAlign: "center" }}
                        >
                          {weightAnalysis.totalDataPoints} data point
                          {weightAnalysis.totalDataPoints !== 1 ? "s" : ""} in
                          period
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="h6" fontWeight="bold">
                      No Weight Data
                    </Typography>
                  )}
                </CardContent>
              </MetricCard>
            </Grid>
            <Grid item xs={6} sm={4} md>
              <MetricCard>
                <CardContent sx={{ p: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Goal & Check-in Day
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="h6" fontWeight="bold">
                      {clientData.statistics?.goal || "Not Set"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textTransform: "capitalize" }}
                    >
                      Check-in: {clientData.checkInDay || "Not Set"}
                    </Typography>
                  </Stack>
                </CardContent>
              </MetricCard>
            </Grid>
            <Grid item xs={6} sm={4} md>
              <MetricCard>
                <CardContent sx={{ p: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Compliance Score
                  </Typography>
                  {compliance &&
                  (compliance.overallScore > 0 ||
                    compliance.overallRating > 0) ? (
                    <Stack spacing={1} alignItems="center">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color="primary"
                        >
                          {compliance.overallScore}%
                        </Typography>
                        {compliance.overallRating > 0 && (
                          <Rating
                            value={compliance.overallRating}
                            readOnly
                            size="small"
                            precision={1}
                          />
                        )}
                      </Box>
                      {report.uid && report.organizationId && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <ComplianceTrendIndicator
                            uid={report.uid}
                            organizationId={report.organizationId}
                            showDetails={false}
                          />
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: "center", fontSize: "0.7rem" }}
                      >
                        {compliance.trackedCategories?.length || 0} areas
                        tracked
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {!compliance
                          ? "Loading..."
                          : compliance.overallScore === 0
                            ? "0% - No Activity"
                            : "No Data"}
                      </Typography>
                      {compliance &&
                        compliance.trackedCategories?.length > 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textAlign: "center", fontSize: "0.7rem" }}
                          >
                            {compliance.trackedCategories.length} areas
                            available
                          </Typography>
                        )}
                    </Stack>
                  )}
                </CardContent>
              </MetricCard>
            </Grid>
          </Grid>
        </CardContent>
      </HeaderCard>
    </Box>
  );
};

export default ReportHeader;
